import { ExternalLink, Smartphone } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { trackWelcomeOptionSelected } from '../utils/analytics';
import phoneComputerImage from '../assets/0c05356776c0822e039e635fb6a9f68c1adefd25.png';
import tabsImage from '../assets/a2929e85ed4097a4574e232d833a98023c2d038f.png';

interface WelcomeDesktopInstructionsProps {
  qrCodeUrl: string;
  onOpenConsultation: () => void;
  onStart: () => void;
}

export function WelcomeDesktopInstructions({ qrCodeUrl, onOpenConsultation, onStart }: WelcomeDesktopInstructionsProps) {
  const { t } = useTranslation('welcome');

  // Get consultation ID from root element
  const consultationId = (() => {
    const rootElement = document.getElementById('consultation-wizard-root');
    return rootElement?.getAttribute('data-consultation-id') || 'unknown';
  })();

  // Handle start button click with option tracking
  const handleStart = () => {
    trackWelcomeOptionSelected('option_b_desktop', consultationId);
    onStart();
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6 mb-6">
      {/* Option A - Use Your Phone */}
      <div className="bg-white p-6 border border-[#FFD4C7] rounded">
        <h3 className="font-bold text-[18px] mb-4">{t('instructions.desktop.optionA.title')}</h3>
        <div className="mb-4">
          <img 
            src={phoneComputerImage}
            alt={t('instructions.desktop.optionA.imageAlt')}
            className="w-full h-auto object-contain rounded mb-4"
          />
        </div>
        <ol className="list-decimal list-inside space-y-3 mb-4 text-[16px]">
          <li>
            <a 
              href="https://webgate.ec.europa.eu/cas/login" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-[#4D2200] underline"
            >
              {t('instructions.desktop.optionA.step1.link')}
            </a>
            {t('instructions.desktop.optionA.step1.text')}
          </li>
          <li>{t('instructions.desktop.optionA.step2')}</li>
          <li>{t('instructions.desktop.optionA.step3')}</li>
        </ol>
        {qrCodeUrl && (
          <div className="bg-white p-4 border-2 border-[#FF6F4E] rounded flex items-center justify-center mb-3">
            <div className="flex flex-col items-center">
              <img src={qrCodeUrl} alt={t('instructions.desktop.optionA.qrAlt')} className="w-32 h-32" />
              <p className="text-center text-[16px] mt-2 text-[#0A1402] flex items-center gap-1">
                <Smartphone className="w-4 h-4" />
                {t('instructions.desktop.optionA.scanText')}
              </p>
            </div>
          </div>
        )}
        <button
          onClick={onOpenConsultation}
          className="w-full bg-[#FF6F4E] text-black hover:bg-[#FF6F4E]/90 px-6 font-bold text-[16px] inline-flex items-center justify-center gap-2 transition-colors min-h-[48px]"
        >
          <ExternalLink className="w-5 h-5" />
          {t('instructions.desktop.openConsultation')}
        </button>
      </div>

      {/* Option B - Browser Tabs */}
      <div className="bg-white p-6 border border-[#FFD4C7] rounded flex flex-col">
        <div className="flex-grow">
          <h3 className="font-bold text-[18px] mb-4">{t('instructions.desktop.optionB.title')}</h3>
          <div className="mb-4">
            <img 
              src={tabsImage}
              alt={t('instructions.desktop.optionB.imageAlt')}
              className="w-full h-auto object-contain rounded mb-4"
            />
          </div>
          <ol className="list-decimal list-inside space-y-3 mb-6 text-[16px]">
            <li>
              <a 
                href="https://webgate.ec.europa.eu/cas/login" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-[#4D2200] underline"
              >
                {t('instructions.desktop.optionB.step1.link')}
              </a>
              {t('instructions.desktop.optionB.step1.text')}
            </li>
            <li>{t('instructions.desktop.optionB.step2')}</li>
          </ol>
        </div>
        <div className="space-y-3 mt-auto">
          <button
            onClick={onOpenConsultation}
            className="w-full bg-[#FF6F4E] text-black hover:bg-[#FF6F4E]/90 px-6 font-bold text-[16px] inline-flex items-center justify-center gap-2 transition-colors min-h-[48px]"
          >
            <ExternalLink className="w-5 h-5" />
            {t('instructions.desktop.openConsultation')}
          </button>
          <button
            onClick={handleStart}
            className="w-full bg-primary text-primary-foreground px-6 hover:bg-primary/90 transition-colors font-bold text-[16px] flex items-center justify-center min-h-[48px]"
          >
            {t('instructions.desktop.startGuide')}
          </button>
        </div>
      </div>
    </div>
  );
}

